import React, { useCallback, useEffect, useState } from 'react';
import {
    Button,
    ButtonTheme,
    DropDownBox,
    DropDownBoxComponentSpacing,
    ShowIf,
    TextBox,
    ToggleButton,
    ToggleButtonItem,
    ToggleButtonLabelSize,
    YesNoToggleButton,
} from '@apply/components';
import { ButtonGroupTypes } from 'devextreme-react/button-group';
import {
    useDeleteOfficer,
    useOfficersQuery,
    useSaveOfficer,
} from '../queries/businessProfileQueries';
import { Officer, WorkTimeType } from '../types';
import { PersonNameFields } from '../components/PersonNameFields';
import { SummaryCard, SummaryRow } from '../components/SummaryCard';
import { FormModal } from '../components/FormModal';
import { OFFICER_TITLE_OPTIONS } from '../constants/options';
import { StepErrorBanner } from '../components/StepErrorBanner';
import { StepStatus } from '../components/StepStatus';
import { useActionError } from '../hooks/useActionError';
import { RegisterStep } from '../wizard/stepApi';
import { asInput } from '../components/inputValue';
import { useItemEditor } from '../hooks/useItemEditor';

interface OtherOfficersStepProps {
    activeEntityId: number | null;
    onRegister: RegisterStep;
}

const WORK_TIME_ITEMS: ToggleButtonItem[] = [
    { id: 'Full', text: 'Full', hint: 'Full time' },
    { id: 'Part', text: 'Part', hint: 'Part time' },
];

export const OtherOfficersStep: React.FC<OtherOfficersStepProps> = ({ activeEntityId, onRegister }) => {
    const entityId = activeEntityId ?? 0;
    const { data: officers, isLoading, isError, error, refetch } = useOfficersQuery(entityId);
    const { mutateAsync: saveOfficer } = useSaveOfficer();
    const { mutateAsync: deleteOfficer } = useDeleteOfficer(entityId);
    const rowAction = useActionError();

    const [hasOthers, setHasOthers] = useState<boolean | null>(null);
    const existing = officers ?? [];

    const makeEmpty = useCallback(
        (): Officer => ({
            id: 0,
            entityId,
            firstName: '',
            middleName: '',
            lastName: '',
            suffix: '',
            officerTitle: '',
            phone: '',
            email: '',
            workTime: null,
            isWillingGuarantor: null,
            lastEditedUtc: null,
        }),
        [entityId],
    );

    const editor = useItemEditor<Officer>({
        makeEmpty,
        save: (officer) => saveOfficer({ ...officer, entityId }),
    });

    useEffect(() => {
        if (existing.length > 0 && hasOthers === null) {
            setHasOthers(true);
        }
    }, [existing.length, hasOthers]);

    // Officers are saved from the popup, so Next has nothing to persist
    const save = useCallback(async () => undefined, []);

    useEffect(() => {
        onRegister({ save, canAdvance: hasOthers === false || existing.length > 0 });
    }, [save, hasOthers, existing.length, onRegister]);

    const update = <K extends keyof Officer>(key: K, value: Officer[K]) =>
        editor.setDraft((prior) => ({ ...prior, [key]: value }));

    const rows: SummaryRow[] = existing.map((officer) => ({
        key: String(officer.id),
        title: `${officer.firstName} ${officer.lastName}`.trim(),
        subtitle: officer.officerTitle || 'Officer',
        actions: [
            { label: 'Edit Details', onClick: () => editor.openEdit(officer) },
            {
                label: 'Delete Officer',
                destructive: true,
                onClick: () => {
                    void rowAction.run(() => deleteOfficer(officer.id));
                },
            },
        ],
    }));

    if (isLoading || isError) {
        return (
            <StepStatus
                isLoading={isLoading}
                isError={isError}
                error={error}
                onRetry={() => void refetch()}
            />
        );
    }

    return (
        <div className='bp-step'>
            {rowAction.error != null && (
                <StepErrorBanner
                    error={rowAction.error}
                    onDismiss={rowAction.clear}
                />
            )}
            <ShowIf showIf={existing.length === 0}>
                <YesNoToggleButton
                    id='bpHasOtherOfficers'
                    value={hasOthers ?? undefined}
                    onItemClick={(e: ButtonGroupTypes.ItemClickEvent) =>
                        setHasOthers((e.itemData as ToggleButtonItem)?.id === 'Yes')
                    }
                    width='230px'
                    height='40px'
                    label='Are there any Corporate Officers not already provided in (3) Owners of Borrower Entity? E.g. a CFO.'
                    labelSize={ToggleButtonLabelSize.Medium}
                />
            </ShowIf>

            <ShowIf showIf={existing.length > 0}>
                <SummaryCard
                    id='bpOfficers'
                    heading='Officers'
                    rows={rows}
                />
            </ShowIf>

            <ShowIf showIf={hasOthers === true}>
                <div style={{ marginTop: 24 }}>
                    <Button
                        id='bpAddOfficerButton'
                        dataTestid='bpAddOfficerButton'
                        theme={existing.length === 0 ? ButtonTheme.Primary : ButtonTheme.Secondary}
                        width='100%'
                        maxWidth='346px'
                        handleClick={editor.openAdd}
                    >
                        {existing.length === 0 ? 'Add Officer' : 'Add Another Officer'}
                    </Button>
                </div>
            </ShowIf>

            <FormModal
                id='bpOfficerModal'
                isOpen={editor.isOpen}
                title={editor.isEditing ? 'Edit Officer' : 'Add Officer'}
                onSave={editor.commit}
                onClose={editor.close}
            >
                <PersonNameFields
                    idPrefix='bpOfficer'
                    value={editor.draft}
                    requiredFields
                    onChange={(patch) => editor.setDraft((prior) => ({ ...prior, ...patch }))}
                />

                <DropDownBox
                    id='bpOfficerTitle'
                    value={asInput(editor.draft.officerTitle)}
                    onChange={(v) => update('officerTitle', v)}
                    label='Officer Title'
                    dataSource={OFFICER_TITLE_OPTIONS}
                    valueExpr='value'
                    displayExpr='display'
                    componentSpacing={DropDownBoxComponentSpacing.SmallSpacing}
                />

                <TextBox
                    id='bpOfficerPhone'
                    value={asInput(editor.draft.phone)}
                    onChange={(v) => update('phone', v)}
                    label='Phone Number'
                    type='tel'
                    maxLength={20}
                />

                <TextBox
                    id='bpOfficerEmail'
                    value={asInput(editor.draft.email)}
                    onChange={(v) => update('email', v)}
                    label='Email Address'
                    type='email'
                    maxLength={200}
                />

                <div style={{ marginTop: 20 }}>
                    <ToggleButton
                        id='bpOfficerWorkTime'
                        items={WORK_TIME_ITEMS}
                        selectedItems={
                            editor.draft.workTime === null ? [] : [WorkTimeType[editor.draft.workTime]]
                        }
                        selectionMode={editor.draft.workTime === null ? 'none' : 'single'}
                        stylingMode='outlined'
                        onItemClick={(e: ButtonGroupTypes.ItemClickEvent) =>
                            update(
                                'workTime',
                                (e.itemData as ToggleButtonItem)?.id === 'Full'
                                    ? WorkTimeType.Full
                                    : WorkTimeType.Part,
                            )
                        }
                        width='230px'
                        height='40px'
                        label='Is this person full time or part time?'
                        labelSize={ToggleButtonLabelSize.Medium}
                    />
                </div>

                <div style={{ marginTop: 20 }}>
                    <YesNoToggleButton
                        id='bpOfficerGuarantor'
                        value={editor.draft.isWillingGuarantor ?? undefined}
                        onItemClick={(e) =>
                            update('isWillingGuarantor', (e.itemData as ToggleButtonItem)?.id === 'Yes')
                        }
                        width='230px'
                        height='40px'
                        label='Is this person willing to be a Guarantor?'
                        labelSize={ToggleButtonLabelSize.Medium}
                    />
                </div>
            </FormModal>
        </div>
    );
};
